import { DialogComponent } from '@theme/dialog';
import { CartUpdateEvent, ThemeEvents } from '@theme/events';

/**
 * @typedef {Event & {
 *   detail?: {
 *     data?: {
 *       didError?: boolean,
 *       source?: string,
 *       itemCount?: number,
 *       itemCountDelta?: number,
 *       sections?: Record<string, string>
 *     }
 *     resource?: {
 *       item_count?: number
 *     }
 *   }
 * }} CartUpdateLikeEvent
 */

/**
 * A custom element that manages a cart drawer.
 *
 * @extends {DialogComponent}
 */
class CartDrawerComponent extends DialogComponent {
  connectedCallback() {
    super.connectedCallback();
    document.addEventListener(ThemeEvents.cartUpdate, this.#handleCartUpdate);
    window.addEventListener('pageshow', this.#handlePageShow);
  }

  disconnectedCallback() {
    super.disconnectedCallback();
    document.removeEventListener(ThemeEvents.cartUpdate, this.#handleCartUpdate);
    window.removeEventListener('pageshow', this.#handlePageShow);
  }

  /**
   * iOS back-swipe restores pages from bfcache with stale cart drawer markup.
   * Refresh the cart section and count from Shopify so the header and drawer
   * agree without requiring a full page reload.
   *
   * @param {PageTransitionEvent} event
   */
  #handlePageShow = (event) => {
    if (!event.persisted) return;

    this.#refreshCartState();
  };

  async #refreshCartState() {
    const cartItemsComponent = this.querySelector('cart-items-component');
    const sectionId = cartItemsComponent instanceof HTMLElement ? cartItemsComponent.dataset.sectionId : '';
    if (!sectionId) return;

    try {
      const sectionUrl = new URL(window.location.href);
      sectionUrl.searchParams.set('section_id', sectionId);

      const [cartResponse, sectionResponse] = await Promise.all([fetch('/cart.js'), fetch(sectionUrl.toString())]);
      if (!cartResponse.ok || !sectionResponse.ok) return;

      const cart = await cartResponse.json();
      const sectionHTML = await sectionResponse.text();

      document.dispatchEvent(
        new CartUpdateEvent(cart, 'pageshow-cart-sync', {
          source: 'pageshow-cart-sync',
          itemCount: cart.item_count,
          sections: {
            [sectionId]: sectionHTML,
          },
        })
      );
    } catch (error) {
      console.error('Failed to refresh cart state after page restore:', error);
    }
  }

  /**
   * Handles optimistic cart drawer updates for add-to-cart flows that do not
   * originate from the drawer's own cart item quantity controls.
   * @param {CartUpdateLikeEvent} event
   */
  #handleCartUpdate = (/** @type {CartUpdateLikeEvent} */ event) => {
    if (event.detail?.data?.didError) return;
    if (event.detail?.data?.source !== 'product-form-component') return;

    this.#updateResultsCount(event);

    if (this.hasAttribute('auto-open')) {
      this.showDialog();
    }
  };

  /**
   * @param {CartUpdateLikeEvent} event
   */
  #updateResultsCount(/** @type {CartUpdateLikeEvent} */ event) {
    const results = this.querySelector('.cart_results');

    if (!(results instanceof HTMLElement)) return;

    const cartItemsComponent = this.querySelector('cart-items-component');
    const sectionId = cartItemsComponent instanceof HTMLElement ? cartItemsComponent.dataset.sectionId : undefined;
    const sectionHTML = sectionId ? event.detail?.data?.sections?.[sectionId] : null;
    const absoluteCount = sectionHTML ? this.#getResultsCountFromSection(sectionHTML) : null;

    if (absoluteCount !== null) {
      results.textContent = `${absoluteCount} Results`;
      return;
    }

    const cartCount = Number(event.detail?.resource?.item_count ?? event.detail?.data?.itemCount);
    if (Number.isFinite(cartCount)) {
      results.textContent = `${cartCount} Results`;
      return;
    }

    const incomingCount = Number(event.detail?.data?.itemCountDelta);
    if (!Number.isFinite(incomingCount)) return;

    const currentCount = Number.parseInt(results.textContent ?? '0', 10) || 0;
    const nextCount = currentCount + incomingCount;

    results.textContent = `${nextCount} Results`;
  }

  /**
   * @param {string} sectionHTML
   * @returns {number | null}
   */
  #getResultsCountFromSection(sectionHTML) {
    const section = new DOMParser().parseFromString(sectionHTML, 'text/html');
    const results = section.querySelector('.cart_results');
    const hiddenCount = section.querySelector('[ref="cartItemCount"]');
    const countText = results?.textContent ?? hiddenCount?.textContent ?? '';
    const count = Number.parseInt(countText, 10);

    return Number.isFinite(count) ? count : null;
  }

  open() {
    this.showDialog();

    /**
     * Close cart drawer when installments CTA is clicked to avoid overlapping dialogs
     */
    customElements.whenDefined('shopify-payment-terms').then(() => {
      const installmentsContent = document.querySelector('shopify-payment-terms')?.shadowRoot;
      const cta = installmentsContent?.querySelector('#shopify-installments-cta');
      cta?.addEventListener('click', this.closeDialog, { once: true });
    });
  }

  close() {
    this.closeDialog();
  }
}

if (!customElements.get('cart-drawer-component')) {
  customElements.define('cart-drawer-component', CartDrawerComponent);
}
